/**
 * EntityProxyNodeView
 *
 * Pure engine view for entity-proxy nodes. Renders three lifecycle states
 * (real-confident, real-tentative, virtual) per `schema.md §2.4`.
 *
 * Virtual nodes arrive in this view through one of two paths:
 *   1. Agent emission — Cortex agent emits a Yjs op with isVirtual=true,
 *      virtualTitle='...', tentative=true. The op flows through the canvas
 *      WebSocket stream, lands in the Y.Doc nodes Map, and renders here.
 *   2. (reserved) Future paste flows that explicitly request virtual placeholders.
 *      As of Canvas S11, plain-text paste creates real-tentative or no-match
 *      text nodes — never virtuals. See useResolverOnPaste.
 *
 * The click-to-promote path is identical regardless of origin: clicking the
 * card calls `onTriggerVirtualPromotion(nodeId)`, which the shell
 * (ConnectedEntityProxyNode) wires to `promoteVirtualNode(...)` against
 * `POST /api/canvas/:id/promote-virtual`. On success the server writes
 * back to Yjs with origin 'server:promotion', flipping isVirtual=false and
 * setting entityId — the WS sync delivers the change automatically.
 */
import { memo } from 'react';
import { Flex, Text, Button, Spin } from '@nula/core-ui';
import clsx from 'clsx';
import type { EntityProxyData, ResolvedEntity } from '@/features/canvas/engine/schema/types';
import styles from './entity-proxy-node-view.module.css';
import { resolveNodeAriaLabel } from '../a11y/aria-labels';
import { useParentLabelResolver } from '../a11y/parent-label-context';

export interface EntityProxyNodeViewProps {
  nodeId: string;
  data: EntityProxyData;
  /** Live entity row from `entityStore`, or null if not (yet) resolvable. */
  resolved: ResolvedEntity | null;
  selected: boolean;
  /** Optional parent frame id from React Flow's NodeProps; used for "(in <frame>)" suffix. */
  parentId?: string | null;
  /**
   * When true, renders a loading spinner during the brief in-flight window
   * between clicking Promote and the server's 'server:promotion' writeback
   * arriving via Yjs sync. Shell sets this via useState in ConnectedEntityProxyNode.
   * Optional for back-compat with S5 callers.
   */
  promoting?: boolean;
  /**
   * Fired when the user clicks "Promote" on a virtual proxy. The shell wires
   * this to the actual `POST /api/canvas/:id/promote-virtual` endpoint (S11).
   * Single-arg (nodeId) — engine does not know about canvasId; the shell
   * binds canvasId from its context when constructing this callback.
   */
  onTriggerVirtualPromotion?: (nodeId: string) => void;
}

type LifecycleState = 'real-confident' | 'real-tentative' | 'virtual';

function deriveLifecycleState(data: EntityProxyData): LifecycleState {
  if (data.isVirtual) return 'virtual';
  if (data.tentative) return 'real-tentative';
  return 'real-confident';
}

/**
 * Pure engine view for the entity-proxy node type. Data-only props — does NOT
 * import `entityStore`, does NOT call any HTTP, does NOT know about routes.
 *
 * Three lifecycle states (per schema.md §2.4):
 *   - real-confident: data.isVirtual=false, data.tentative=false, resolved≠null
 *   - real-tentative: data.isVirtual=false, data.tentative=true, resolved≠null
 *   - virtual:         data.isVirtual=true (entityId always null in this state)
 *
 * Plus the cached-fallback path:
 *   - resolved=null, data.isVirtual=false (entity not yet projected into store)
 *
 * The shell wrapper (ConnectedEntityProxyNode) reads entityStore and passes
 * `resolved` down.
 *
 * Raw-HTML exception: React Flow v12 expects <div> containers for custom nodes.
 * Sub-content uses core-ui primitives (Flex, Text, Button).
 *
 * Engine/shell seam: this file MUST NOT import from @sync, @adapters, @services,
 * @routes, @session, @stores, or @features/canvas/components/shell. Task 12 enforces this
 * with an audit test. The seam is load-bearing for S11's engine packaging.
 */
export const EntityProxyNodeView = memo(function EntityProxyNodeView({
  nodeId,
  data,
  resolved,
  selected,
  parentId,
  promoting,
  onTriggerVirtualPromotion,
}: EntityProxyNodeViewProps) {
  const lifecycle = deriveLifecycleState(data);
  const isCached = !data.isVirtual && resolved === null;
  const parentLabel = useParentLabelResolver();
  // Compute the displayed name once so the aria-label and the rendered Text
  // can stay in lockstep across virtual/cached/resolved branches.
  const displayedName =
    resolved?.title ||
    (data.isVirtual ? data.virtualTitle : data.cachedTitle) ||
    '(untitled)';
  const ariaLabel = resolveNodeAriaLabel(
    { type: 'entity-proxy', data: { entityName: displayedName }, parentId: parentId ?? null },
    parentLabel,
  );

  // Virtual state: render virtualTitle + Draft pill + optional Promote button.
  if (lifecycle === 'virtual') {
    return (
      <div
        data-testid="entity-proxy-node"
        data-state="virtual"
        data-node-id={nodeId}
        data-entity-id={data.entityId ?? ''}
        data-is-virtual="true"
        data-tentative={String(data.tentative)}
        data-cached="false"
        data-selected={String(selected)}
        aria-label={ariaLabel}
        className={clsx(styles.proxy, styles.tentative, styles.virtual)}
      >
        <div className={styles.body}>
          <Flex vertical gap="sm">
            <Flex align="center" gap="xs">
              <Text className={styles.title}>
                {data.virtualTitle ?? '(untitled)'}
              </Text>
              <Text size="xs" type="secondary" className={styles.pill}>
                Draft
              </Text>
            </Flex>
            {onTriggerVirtualPromotion && (
              <Flex justify="end" align="center" gap="xs">
                {promoting && (
                  <span aria-label="Promoting" role="status" className={styles.spinnerWrapper}>
                    <Spin size="sm" spinning />
                  </span>
                )}
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => onTriggerVirtualPromotion(nodeId)}
                  aria-label={`Promote draft "${data.virtualTitle ?? '(untitled)'}" to a real entity`}
                  disabled={promoting}
                >
                  Promote
                </Button>
              </Flex>
            )}
          </Flex>
        </div>
      </div>
    );
  }

  // Real states (confident or tentative): render live or cached title/snippet.
  // displayedName (computed at top) already encodes the title fallback chain;
  // reuse it here so the aria-label and the visible Text stay in lockstep.
  const displaySnippet =
    resolved?.snippet ?? (isCached ? data.cachedSnippet : null);

  return (
    <div
      data-testid="entity-proxy-node"
      data-state={lifecycle}
      data-node-id={nodeId}
      data-entity-id={data.entityId ?? ''}
      data-is-virtual="false"
      data-tentative={String(data.tentative)}
      data-cached={String(isCached)}
      data-selected={String(selected)}
      aria-label={ariaLabel}
      className={clsx(styles.proxy, data.tentative && styles.tentative)}
    >
      <div className={styles.body}>
        <Flex vertical gap="sm">
          <Flex align="center" gap="xs">
            <Text className={styles.title}>
              {displayedName}
            </Text>
            {data.tentative && (
              <Text size="xs" type="secondary" data-testid="tentative-badge" className={styles.pill}>
                Tentative
              </Text>
            )}
          </Flex>
          {data.showPreview && displaySnippet ? (
            <Text
              size="xs"
              type="secondary"
              data-testid="entity-proxy-snippet"
              className={styles.snippet}
            >
              {displaySnippet}
            </Text>
          ) : null}
        </Flex>
      </div>
    </div>
  );
});
